import React, { useEffect, useState } from 'react';
import { Row, Col, Button, message, Space, Popconfirm } from 'antd';
import ListCard from 'Molecules/ListCard';
import HeadingChip from 'Molecules/HeadingChip';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrencies } from '../ducks/actions'
import { removeCurrency, bulkRemoveCurrencies } from '../ducks/services'
import ActionButton from 'Molecules/ActionButton';
import Search from './components/Search';
import AllRoles from '../../../../routing/config/AllRoles';
import { allowed } from '../../../../routing/config/utils';
import { getListQueryItems } from 'Modules/Application/ducks/actions';
import { useTranslate } from 'Translate';

export default (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currencies = useSelector((state) => state.systems.currencies);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchVal, setSearchVal] = useState(null);
  const allowedEdit = allowed([AllRoles.SYSTEM.CURRENCY], 'write');
  const allowedDelete = allowed([AllRoles.SYSTEM.CURRENCY], 'delete');
  const globalListQuery = useSelector((state) => state.global.listQueryItems);
  const i18n = useTranslate();
  const { t } = i18n;

  const colName = [
    {
      title: t("General.code"),
      dataIndex: 'code',
      key: 'code',
      className: 'enable-click',
      sorter: true
    },
    {
      title: t("General.symbol"),
      dataIndex: 'symbol',
      key: 'symbol',
      className: 'enable-click',
      sorter: true
    },
    {
      title: t("System.external_id"),
      dataIndex: 'reference',
      key: 'reference',
      className: 'enable-click',
      sorter: true
    },
    {
      title: t("General.status"),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (text, record) =>
        <div className='flex align-items-center'>
          <span className={text === "Active" ? "c-success" : "c-danger"}>
            {t(`General.${text?.toLowerCase()}`)}
          </span>
          {
            allowedEdit &&
            <Button type="link" onClick={() => onAction(record.id, text)}>
              {text === "Active" ? `(${t("General.deactivate")})` : `(${t("General.activate")})`}
            </Button>
          }
        </div>
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      render: (text, record) =>
        <ActionButton
          title="currency"
          btnAction1={() => navigate(`/currency/${record.id}`)}
          recordId={record.id}
          onRemove={(allowedDelete && !record.reference) && onRemove}
          setLoading={props.setLoading}
          loading={props.loading}
        />
    }
  ]

  const addNew = () => navigate('/currency/add');

  const btnList = [
    {
      title: t("System.confirm_remove_select_currency"),
      text: t("General.bulk_remove"),
      classes: 'red-btn text-white attendance-success',
      permit: true,
      action: () => onBulkRemove(),
    },
    {
      text: `+ ${t("System.new_currency")}`,
      action: () => addNew(),
    },
  ];

  useEffect(() => {
    if(!globalListQuery.currencyListQuery) {
      dispatch(getCurrencies(1, limit, '', '', null, props.setLoading));
      dispatch(getListQueryItems('currencyListQuery', 1, limit, '', '', null, props.setLoading));
    } else {
      const {page, limit, sortOrder, sortColKey, searchValue} = globalListQuery.currencyListQuery
      dispatch(getCurrencies(page, limit, sortOrder, sortColKey, searchValue, props.setLoading));
      setPage(page);
      setLimit(limit);
      setSearchVal(searchValue);
    }
  }, []);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const onAction = async (vendorId, status) => {
    props.setLoading(true);

    const payload = {
      currency_id: vendorId,
      status_event: status === 'Active' ? 'deactivate' : 'activate'
    }

    await removeCurrency(payload).then(res => {
      props.setLoading(false);
      if (res.data.code === 200) {
        message.success(t("System.currency_status_update_success"));
        setTimeout(() => dispatch(getCurrencies(page, limit, '', '', searchVal, props.setLoading)), 500);
        dispatch(getListQueryItems('currencyListQuery', page, limit, '', '', searchVal, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onRemove = async (id) => {
    props.setLoading(true);

    const payload = {
      currency_id: id,
      status_event: 'remove'
    }

    await removeCurrency(payload).then(res => {
      props.setLoading(false);
      if(res.data.code === 200){
        message.success(t("System.currency_remove_success"));
        setTimeout(() => dispatch(getCurrencies(page, limit, '', '', searchVal, props.setLoading)), 500);
        dispatch(getListQueryItems('currencyListQuery', page, limit, '', '', searchVal, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onBulkRemove = async () => {
    props.setLoading(true);

    const payload = {
      currencies: JSON.stringify(selectedRowKeys)
    }

    await bulkRemoveCurrencies(payload).then(res => {
      props.setLoading(false);
      if(res.data.code === 200){
        rowSelection.onChange([]);
        message.success(t("System.currencies_remove_success"));
        setPage(1);
        setSearchVal(null);
        setTimeout(() => dispatch(getCurrencies(1, limit, '', '', null, props.setLoading)), 500);
        dispatch(getListQueryItems('currencyListQuery', 1, limit, '', '', null, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setLimit(pagination.pageSize);
    if (sorter.order) {
      dispatch(getCurrencies(pagination.current, pagination.pageSize, sorter.order, sorter.columnKey, searchVal, props.setLoading));
      dispatch(getListQueryItems('currencyListQuery', pagination.current, pagination.pageSize, sorter.order, sorter.columnKey, searchVal, props.setLoading))
    } else {
      dispatch(getCurrencies(pagination.current, pagination.pageSize, '', '', searchVal, props.setLoading));
      dispatch(getListQueryItems('currencyListQuery', pagination.current, pagination.pageSize, '', '', searchVal, props.setLoading))
    }
  }

  const onSearch = (search) => {
    setPage(1);
    if (search) {
      let searching = {};
      searching = {
        name: search.name,
        code: search.code
      };
      setSearchVal(searching);
      dispatch(getCurrencies(1, limit, '', '', searching, props.setLoading));
      dispatch(getListQueryItems('currencyListQuery', 1, limit, '', '', searching, props.setLoading))
    } else {
      setSearchVal(null);
      dispatch(getCurrencies(1, limit, '', '', null, props.setLoading));
      dispatch(getListQueryItems('currencyListQuery', 1, limit, '', '', null, props.setLoading))
    }
  };

  const onClickRow = (record) => {
    return {
      onClick: (e) => {
        if (e.target.closest(".enable-click"))
        navigate(`/currency/${record.id}`)
      },
    };
  };

  return (
    <>
      <Row gutter={[10, 10]}>
        <Col span={24}>
          <HeadingChip title={t("System.currencies")} btnList={allowedEdit ? btnList : null} btnHidden={selectedRowKeys.length <= 0} />
        </Col>
        <Col span={24} className="clickRow">
          <ListCard
            onRow={onClickRow}
            Search={Search}
            onSearch={onSearch}
            searchVal={globalListQuery.currencyListQuery?.searchValue}
            ListData={currencies?.list?.map(x => ({...x, key: x.id}))}
            ListCol={colName}
            rowSelection={allowedEdit && rowSelection}
            onChange={onTableChange}
            pagination={{
              total: currencies?.total_count,
              current: page,
              pageSize: limit
            }}
          />
        </Col>
      </Row>
    </>
  )
}